import { computed, signal } from '@preact/signals';
import { pct } from '@shared/format';
import { HEARD_VISIBLE } from '@shared/game/heat';
import { shareText } from '@shared/game/share';
import { clueProblem, norm } from '@shared/game/text';
import type { BoardSummary, GameView, Lang, Turn } from '@shared/types';
import { type Api, ApiError } from '../api/client';
import { clueProblemMessage, errorMessage, t } from '../i18n/ru';
import type { HapticKind } from '../lib/haptics';
import type { BoardProgress, Persist } from '../lib/persist';
import type { CardData } from '../lib/share-card';

export type HintKind = '' | 'err' | 'ok';
export interface Hint {
  text: string;
  kind: HintKind;
}
/** Один «процент нагрева», летящий к бомбе: услышанная подсказка или штраф за обманку. */
export interface HeatPart {
  kind: 'heard' | 'decoy';
  text: string;
}
/** Полёт нагрева от открытого слова к бомбе (рисует `HeatFlight`). `ms` — сколько он длится. */
export interface Flight {
  id: number;
  pick: number;
  ms: number;
  parts: HeatPart[];
}
/** Визуальные эффекты последнего хода. */
export interface Fx {
  pop: number | null;
  float: { id: number; text: string } | null;
  boom: boolean;
  flight: Flight | null;
}
export type LoadStatus = 'loading' | 'ready' | 'error';
export interface GenerationUi {
  step: 1 | 2 | 3;
  note: string;
  failed: boolean;
}
export type ModalName = 'help' | 'settings' | 'levels';

export interface ControllerDeps {
  api: Api;
  persist: Persist;
  sleep: (ms: number) => Promise<void>;
  /** false — без пауз между шагами анимации (prefers-reduced-motion, тесты). */
  animate: boolean;
  pollMs: number;
  now: () => number;
  /** Тактильный отклик (вибрация на телефоне). Не задан — без него. */
  haptic?: (kind: HapticKind) => void;
  /** Длительность полёта нагрева к бомбе, мс. 0 или не задана — без полёта (reduced motion, старый браузер, тесты). */
  flightMs?: number;
  /** Поле из ссылки «Поделиться» (?b=…): открывается первым, если есть в каталоге. */
  initialBoard?: string | null;
  /** Ссылка на поле для «Поделиться». Не задана — текст уходит без ссылки. */
  shareUrl?: (boardId: string) => string;
}

const NO_HINT: Hint = { text: '', kind: '' };
const NO_FX: Fx = { pop: null, float: null, boom: false, flight: null };
const ANALYSIS_POLLS = 8;
const GENERATION_TIMEOUT_MS = 6 * 60 * 1000;

/**
 * Состояние и сценарии игры на клиенте. Вся механика (нагрев, выбор слова) — на сервере;
 * здесь только показ, анимация ходов, навигация и локальные данные игрока.
 */
export class GameController {
  readonly lang = signal<Lang>('ru');
  readonly boards = signal<BoardSummary[]>([]);
  readonly game = signal<GameView | null>(null);
  readonly status = signal<LoadStatus>('loading');
  readonly loadError = signal('');
  readonly busy = signal(false);
  readonly hint = signal<Hint>(NO_HINT);
  readonly fx = signal<Fx>(NO_FX);
  readonly best = signal<number | null>(null);
  /** true, если только что закончившаяся партия побила рекорд поля (для значка на экране итога). */
  readonly record = signal(false);
  /** Пройденные поля каталога (есть победа): id → лучший результат. Основа «карты полей». */
  readonly progress = signal<Record<string, BoardProgress>>({});
  readonly generation = signal<GenerationUi | null>(null);
  readonly modal = signal<ModalName | null>(null);
  readonly settingsNote = signal('');

  /** Номер поля в каталоге (1-based) и размер каталога. */
  readonly position = computed(() => {
    const g = this.game.value;
    const k = g ? this.boards.value.findIndex((b) => b.id === g.boardId) : -1;
    return { index: k + 1, total: this.boards.value.length };
  });

  /** Сколько полей каталога уже пройдено. */
  readonly passedCount = computed(
    () => this.boards.value.filter((b) => this.progress.value[b.id] != null).length,
  );

  private epoch = 0;
  private floatId = 0;

  constructor(private readonly deps: ControllerDeps) {}

  private get api(): Api {
    return this.deps.api;
  }
  private get persist(): Persist {
    return this.deps.persist;
  }
  private pause(ms: number): Promise<void> {
    return this.deps.animate ? this.deps.sleep(ms) : Promise.resolve();
  }
  private setHint(text: string, kind: HintKind = ''): void {
    this.hint.value = { text, kind };
  }

  // ---------- запуск и навигация ----------

  /** Первый запуск: язык → каталог → последнее поле; правила показываем один раз. */
  async init(): Promise<void> {
    this.lang.value = this.persist.getLang();
    if (!this.persist.seenHelp()) this.modal.value = 'help';
    try {
      await this.loadBoards();
    } catch (e) {
      this.fail(e);
      return;
    }
    const linked = this.deps.initialBoard;
    const start = linked && this.boards.value.some((b) => b.id === linked) ? linked : null;
    await this.openBoard(start ?? this.persist.getLastBoard(this.lang.value));
  }

  private fail(e: unknown): void {
    this.loadError.value = e instanceof ApiError ? errorMessage(e.code, e.problem) : t.loadFailed;
    this.status.value = 'error';
  }

  private async loadBoards(): Promise<void> {
    this.boards.value = await this.api.listBoards(this.lang.value);
    this.refreshProgress();
  }

  /** Перечитывает из localStorage, какие поля каталога пройдены. */
  private refreshProgress(): void {
    const next: Record<string, BoardProgress> = {};
    for (const b of this.boards.value) {
      const p = this.persist.getProgress(b.id);
      if (p) next[b.id] = p;
    }
    this.progress.value = next;
  }

  /** Открывает поле и начинает партию (или возвращает к незаконченной, если она была). */
  async openBoard(id?: string | null, options: { fresh?: boolean } = {}): Promise<void> {
    const epoch = ++this.epoch;
    this.status.value = 'loading';
    this.setHint('');
    this.fx.value = NO_FX;
    this.record.value = false;
    try {
      if (!this.boards.value.length) await this.loadBoards();
      const boards = this.boards.value;
      const target = boards.find((b) => b.id === id) ?? boards[0];
      if (!target) throw new ApiError('not_found', t.noBoards, 404);

      let game: GameView | null = null;
      const savedId = options.fresh ? null : this.persist.getGameId(target.id);
      if (savedId) {
        try {
          const saved = await this.api.getGame(savedId);
          if (saved.status === 'play' && saved.boardId === target.id) game = saved;
        } catch (e) {
          if (!(e instanceof ApiError && e.code === 'not_found')) throw e;
        }
      }
      game ??= await this.api.createGame(target.id);
      if (epoch !== this.epoch) return;

      this.persist.setLastBoard(this.lang.value, target.id);
      this.persist.setGameId(target.id, game.status === 'play' ? game.id : null);
      this.best.value = this.persist.getBest(target.id);
      this.game.value = game;
      this.status.value = 'ready';
      if (game.status !== 'play') void this.awaitAnalysis(game.id, epoch);
    } catch (e) {
      if (epoch !== this.epoch) return;
      this.fail(e);
    }
  }

  retry(): Promise<void> {
    return this.game.value ? this.openBoard(this.game.value.boardId) : this.init();
  }

  stepBoard(delta: number): Promise<void> {
    const boards = this.boards.value;
    const current = this.game.value?.boardId;
    const k = boards.findIndex((b) => b.id === current);
    if (!boards.length) return this.openBoard(null);
    return this.openBoard(boards[(k + delta + boards.length) % boards.length]?.id);
  }

  playAgain(): Promise<void> {
    return this.openBoard(this.game.value?.boardId, { fresh: true });
  }

  // ---------- ход ----------

  /**
   * Отправляет подсказку. true — ход засчитан (поле ввода можно очистить),
   * false — ошибка: текст остаётся, ход не потрачен.
   */
  async submitClue(raw: string): Promise<boolean> {
    const game = this.game.value;
    if (!game || this.busy.value || game.status !== 'play') return false;
    const clue = norm(raw);
    const problem = clueProblem(game.words, clue);
    if (problem) {
      this.setHint(clueProblemMessage(problem), 'err');
      return false;
    }
    if (game.turns.some((x) => x.clue === clue)) {
      this.setHint(errorMessage('clue_used'), 'err');
      return false;
    }

    const epoch = this.epoch;
    this.busy.value = true;
    this.setHint(t.thinking);
    let result: { turn: Turn; game: GameView };
    try {
      result = await this.api.playTurn(game.id, clue);
    } catch (e) {
      this.busy.value = false;
      const err = e instanceof ApiError ? e : new ApiError('internal', String(e), 0);
      this.setHint(errorMessage(err.code, err.problem), 'err');
      if (err.code === 'not_found') {
        // партия пропала на сервере (например, устарела) — тихо начинаем такую же заново и говорим об этом
        void this.openBoard(game.boardId, { fresh: true }).then(() =>
          this.setHint(errorMessage('not_found'), 'err'),
        );
      }
      return false;
    }
    if (epoch !== this.epoch) {
      this.busy.value = false;
      return false;
    }
    await this.reveal(game, result.turn, result.game, epoch);
    return true;
  }

  private animate(): boolean {
    return this.deps.animate;
  }

  /** Из чего сложился нагрев хода — то, что полетит к бомбе. Пусто, если шкала не двигалась. */
  private heatParts(turn: Turn): HeatPart[] {
    if (turn.role === 'b') return [];
    const parts: HeatPart[] = [];
    if (turn.heard >= HEARD_VISIBLE) parts.push({ kind: 'heard', text: `+${pct(turn.heard)}` });
    if (turn.role === 'd') parts.push({ kind: 'decoy', text: `+${pct(turn.add - turn.heard)}` });
    return parts;
  }

  /** Ответ пришёл целиком, но показываем по шагам: сперва открывается слово, потом греется шкала. */
  private async reveal(prev: GameView, turn: Turn, next: GameView, epoch: number): Promise<void> {
    await this.pause(350);
    this.game.value = { ...prev, revealed: next.revealed };
    // Нагрев «летит» к бомбе, пока шкала стоит на месте; шкала и число обновятся по прилёту
    const parts = this.animate() ? this.heatParts(turn) : [];
    const ms = this.deps.flightMs ?? 0;
    const flight: Flight | null =
      parts.length && ms > 0 ? { id: ++this.floatId, pick: turn.pick, ms, parts } : null;
    this.fx.value = { pop: turn.pick, float: null, boom: false, flight };
    this.deps.haptic?.(turn.role === 't' ? 'good' : turn.role === 'd' ? 'miss' : 'boom');
    await this.pause(flight ? ms : 450);
    if (epoch !== this.epoch) {
      this.busy.value = false;
      return;
    }

    this.game.value = next;
    const heated = turn.role !== 'b' && turn.add >= HEARD_VISIBLE;
    this.fx.value = {
      pop: turn.pick,
      float: heated ? { id: ++this.floatId, text: `+${pct(turn.add)}` } : null,
      boom: next.status === 'lose',
      flight, // след полёта дорисовывается уже после прилёта — не сбрасываем его до следующего хода
    };
    const heard = turn.role !== 'b' && turn.heard >= HEARD_VISIBLE ? pct(turn.heard) : null;
    if (next.status === 'play') {
      if (turn.role === 't') this.setHint(heard ? t.guessedHeard(heard) : t.guessed, 'ok');
      else this.setHint(t.pickedDecoy(next.words[turn.pick] ?? '', heard), 'err');
    } else {
      this.setHint('');
      this.persist.setGameId(next.boardId, null);
      if (next.status === 'win') {
        if (this.persist.saveBest(next.boardId, next.heat)) {
          this.best.value = next.heat;
          this.record.value = true;
        }
        this.refreshProgress();
        this.deps.haptic?.('win');
      }
    }
    this.busy.value = false;
    if (next.status !== 'play' && !next.analysis) void this.awaitAnalysis(next.id, epoch);
  }

  /** Разбор поля считается в фоне на сервере — дожидаемся его для блока «Как можно было сыграть». */
  private async awaitAnalysis(gameId: string, epoch: number): Promise<void> {
    for (let i = 0; i < ANALYSIS_POLLS; i++) {
      if (this.game.value?.analysis) return;
      await this.deps.sleep(this.deps.pollMs);
      if (epoch !== this.epoch || this.game.value?.id !== gameId) return;
      try {
        const g = await this.api.getGame(gameId);
        if (g.analysis) {
          if (epoch === this.epoch && this.game.value?.id === gameId) this.game.value = g;
          return;
        }
      } catch {
        return; // разбор — приятное дополнение, ошибки тут не показываем
      }
    }
  }

  // ---------- генерация нового поля ----------

  async generate(): Promise<void> {
    if (this.generation.value && !this.generation.value.failed) return;
    const started = this.deps.now();
    this.generation.value = { step: 1, note: t.gen.initialNote, failed: false };
    try {
      const job = await this.api.startGeneration(this.lang.value);
      while (true) {
        await this.deps.sleep(this.deps.pollMs);
        const seconds = Math.round((this.deps.now() - started) / 1000);
        if (this.deps.now() - started > GENERATION_TIMEOUT_MS)
          throw new ApiError('ai_timeout', 'timeout', 504);
        const cur = await this.api.getGeneration(job.id);
        if (cur.status === 'failed')
          throw new ApiError(cur.errorCode ?? 'generation_failed', 'generation failed', 200);
        if (cur.status === 'done' && cur.boardId) {
          this.generation.value = { step: 3, note: t.gen.initialNote, failed: false };
          await this.pause(500);
          this.generation.value = null;
          await this.loadBoards();
          await this.openBoard(cur.boardId, { fresh: true });
          return;
        }
        this.generation.value = {
          step: cur.status === 'checking' ? 2 : 1,
          note: t.gen.elapsed(seconds),
          failed: false,
        };
      }
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError('internal', String(e), 0);
      this.generation.value = { step: 1, note: errorMessage(err.code, err.problem), failed: true };
    }
  }

  closeGeneration(): void {
    this.generation.value = null;
  }

  // ---------- настройки и правила ----------

  openSettings(note = ''): void {
    this.settingsNote.value = note || t.settings.note;
    this.modal.value = 'settings';
  }

  /** «Карта полей»: все поля каталога с отметками пройденных. */
  openLevels(): void {
    this.modal.value = 'levels';
  }

  /** Выбор поля на карте: закрываем окно и открываем поле (незаконченная партия возобновится). */
  pickBoard(id: string): Promise<void> {
    this.modal.value = null;
    if (id === this.game.value?.boardId) return Promise.resolve();
    return this.openBoard(id);
  }

  closeModal(): void {
    if (this.modal.value === 'help') this.persist.markHelpSeen();
    this.modal.value = null;
  }

  async saveLang(lang: Lang): Promise<void> {
    this.modal.value = null;
    if (lang === this.lang.value) return;
    this.lang.value = lang;
    this.persist.setLang(lang);
    this.boards.value = [];
    try {
      await this.loadBoards();
    } catch (e) {
      this.fail(e);
      return;
    }
    await this.openBoard(this.persist.getLastBoard(lang));
  }

  // ---------- итоги ----------

  /** Ссылка на текущее поле — друг откроет ровно его. */
  shareLink(): string {
    const g = this.game.value;
    return g && this.deps.shareUrl ? this.deps.shareUrl(g.boardId) : '';
  }

  /** Данные для картинки-результата; null, пока партия не закончена. */
  shareCard(): CardData | null {
    const g = this.game.value;
    if (!g || g.status === 'play') return null;
    const link = this.shareLink();
    let host = '';
    try {
      host = link ? new URL(link).host : '';
    } catch {
      // ссылка не URL — карточка без адреса
    }
    return {
      title: g.title,
      status: g.status,
      heat: g.heat,
      stars: g.stars,
      boom: g.boom,
      roles: g.turns.map((x) => x.role),
      host,
    };
  }

  /** Текст для «Поделиться» (формат — AGENTS.md, раздел 7), без ссылки. */
  shareText(): string {
    const g = this.game.value;
    if (!g) return '';
    return shareText({
      title: g.title,
      status: g.status,
      heat: g.heat,
      stars: g.stars,
      roles: g.turns.map((x) => x.role),
    });
  }
}
